import { useState, useCallback, useEffect } from 'react';

const toElement = (elOrSel) => {
  if (elOrSel instanceof Node) return elOrSel;
  return document.querySelector(elOrSel);
};

const waitForSelector = async (sel) => {
  const element = document.querySelector(sel);
  if (element) return element;
  await new Promise(r => setTimeout(r, 125));
  return waitForSelector(sel);
};

const DEFAULT_SIZE = {};

const getBox = element => element?.getBoundingClientRect() || DEFAULT_SIZE;

/**
 * Get the bounding client rectangle for an element, and trigger a re-render
 * whenever it might change due to user action.
 * @param {string|HTMLElement} elOrSel Element or selector to an element
 * @param {() => () => void} [beforeMeasure] A function to call before measurement,
 *  to ensure the element is present and not hidden.  Returns a teardown function
 * @returns {DOMRect} the bounding client rectangle
 */

const useClientSize = (elOrSel = window.document.documentElement, beforeMeasure) => {
  const e = toElement(elOrSel);
  const [element, setElement] = useState(e);
  const [clientSize, setClientSize] = useState(getBox(element));

  const onChange = useCallback(() => {
    setClientSize(getBox(element));
  }, [element]);

  useEffect(() => {
    if (element !== e) setElement(e);
    if (!e && typeof elOrSel === 'string') {
      (async () => {
        const el = await waitForSelector(elOrSel);
        setElement(el);
        setClientSize(getBox(element));
      })();
    }
  }, [elOrSel, e, element]);

  let undo;
  if (beforeMeasure) {
    undo = beforeMeasure();
  }

  if (undo) undo();
  useEffect(() => {
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange);
    };
  }, [onChange]);
  return clientSize;
};

export default useClientSize;
